import React, { useCallback, useEffect } from "react";
import { Dispatch } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { get, pickBy, sortBy } from "lodash";
import mediator from "@tvg/mediator";
import CrossProductNavigationTemplate from "@fdr/atomic-ui/_templates/CrossProductNavigation";
import { isXSell, openExternalLink } from "@tvg/sh-utils/mobileUtils";
import { getSportsbookRegionCookie } from "@tvg/sh-utils/sessionUtils";
import {
  CrossApplication,
  CrossApplicationProduct
} from "@tvg/ts-types/CrossApp";
import ButtonScrollbar from "@fdr/atomic-ui/_molecules/ButtonScrollbar";
import { TransitionState } from "@tvg/ts-types/Modal";
import { parseJSONCapiMessage } from "@fdr/utils/parseJSONCapiMessage";
import { BinaryFn } from "@tvg/ts-types/Functional";
// @ts-ignore
import { getDeviceOS } from "../utils/isSafari";
// @ts-ignore
import { openCrossProductNavigationModal } from "../../actions/modals";
// @ts-ignore
import { setHasCrossNavigation } from "../../actions/app";

interface Props {
  isModal?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  transitionState?: TransitionState;
}

interface PropsCrossAppContent {
  crossApplications: Array<CrossApplication>;
  onOpenApp: BinaryFn<CrossApplication, string, void>;
}

export interface PropsModal extends PropsCrossAppContent {
  userState: string;
}

export interface PropsList extends PropsCrossAppContent {
  dispatch: Dispatch;
}

const getCrossApplications = (
  crossProducts: CrossApplicationProduct,
  state: string,
  isModal: boolean
): Array<CrossApplication> => {
  const availableApplications = !isModal
    ? pickBy(crossProducts, (app) => app.availableStates.includes(state))
    : crossProducts;
  return sortBy(availableApplications, ["priority"]);
};

const CrossProductNavigationModalContent = ({
  crossApplications,
  userState,
  onOpenApp
}: PropsModal) => (
  <CrossProductNavigationTemplate
    products={crossApplications}
    userState={getSportsbookRegionCookie() || userState}
    onOpenExternalLink={(product) => {
      onOpenApp(product, "All Products");
    }}
  />
);

const CrossProductNavigationList = ({
  crossApplications,
  onOpenApp,
  dispatch
}: PropsList) => (
  <ButtonScrollbar
    keyExtractor={(product) => product.productId}
    buttons={crossApplications}
    buttonType="header"
    onViewAllClick={() => dispatch(openCrossProductNavigationModal())}
    onItemClick={(product) => {
      onOpenApp(product, "Top Header");
    }}
  />
);

export const CrossProductNavigation = ({ isModal }: Props) => {
  const userState: string = useSelector((store) =>
    get(store, "geolocation.state", "")
  );
  const navigationBarProducts = useSelector((store) =>
    parseJSONCapiMessage(store, "capi.messages.navigationBarProducts", {})
  );
  const isNavigationBarEnabled = useSelector((store) =>
    get(store, "capi.featureToggles.isNavigationBarEnabled", false)
  );
  const dispatch = useDispatch();

  const crossProducts = get(navigationBarProducts, "products", {});
  const productsState = getSportsbookRegionCookie() || userState;
  const crossApplications = getCrossApplications(
    crossProducts,
    productsState,
    !!isModal
  );

  const device = getDeviceOS();
  const onOpenApp = useCallback(
    (product: CrossApplication, gtmModule: string) => {
      const { links } = product;
      const storeUrl = get(links, `${device}.url`, "");
      mediator.base.dispatch({
        type: "CROSS_PRODUCT_CLICK",
        payload: {
          module: gtmModule,
          eventLabel: product.title,
          destinationUrl: storeUrl
        }
      });
      openExternalLink(storeUrl);
    },
    [device]
  );

  const showCrossNavigationBar =
    !isXSell() && isNavigationBarEnabled && !!crossApplications?.length;

  useEffect(() => {
    dispatch(setHasCrossNavigation(showCrossNavigationBar));

    return () => dispatch(setHasCrossNavigation(false));
  }, []);

  if (!showCrossNavigationBar) {
    return null;
  }

  return isModal ? (
    <CrossProductNavigationModalContent
      crossApplications={crossApplications}
      userState={userState}
      onOpenApp={onOpenApp}
    />
  ) : (
    <CrossProductNavigationList
      crossApplications={crossApplications}
      onOpenApp={onOpenApp}
      dispatch={dispatch}
    />
  );
};

export default CrossProductNavigation;
